import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {ThemeProvider, CssBaseline, Box} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import createAppTheme from './theme';
import { DarkModeProvider, useDarkMode } from './contexts/DarkModeContext';

import Header from './components/Header';
import Hero from './components/hero/Hero';
import Capabilities from "./components/Capabilities";
import { LanguageAnimationProvider, useLanguageAnimation } from './components/LanguageAnimationContext';

import './localization/i18n';
import Footer from "./components/Footer";
import ContactForm from "./components/contanct/ContactForm";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import About from "./components/pyramid/About";
import Propositions from "./components/proposition/Propositions";
import DocMatePrivacyPolicy from "./docmatePrivacy/DocMatePrivacyPolicy";

const queryClient = new QueryClient();


// Simple Language Animation Overlay Component
const LanguageAnimationOverlay = () => {
    const { animate, setAnimate } = useLanguageAnimation();

    return (
        <AnimatePresence>
            {animate && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    onAnimationComplete={() => setAnimate(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1000,
                        pointerEvents: 'none',
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                    }}
                />
            )}
        </AnimatePresence>
    );
};

// Home Page Component (your existing content)
const HomePage = () => {
    const { animate } = useLanguageAnimation();

    const contentVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
    };

    return (
        <motion.div
            variants={contentVariants}
            initial="initial"
            animate={animate ? 'initial' : 'animate'}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            key={animate ? 'collapsing' : 'expanded'}
        >
            <Header />
            <Hero />

            {/* ——— PROPOSITIONS (Transform Your Business) ——— */}
            <Box
                component="section"
                id="propositions"
                sx={{
                    pb: { xs: 4, sm: 2, md: 0 },
                }}
            >
                <Propositions />
            </Box>

            {/* ——— CAPABILITIES (with top padding on mobile) ——— */}
            <Box
                component="section"
                id="capabilities"
                sx={{
                    pt: { xs: 4, sm: 2, md: 0 },
                }}
            >
                <Capabilities />
            </Box>

            {/* ——— ABOUT (Discover Our Foundation) ——— */}
            <Box
                component="section"
                id="about"
                sx={{
                    pt: { xs: 4, sm: 2, md: 0 },
                }}
            >
                <About />
            </Box>

            <ContactForm setSnackbar={true} />
            <Footer />
        </motion.div>
    );
};

// Main App Content Component with Routes
const AppContent = () => {
    return (
        <>
            <LanguageAnimationOverlay />

            <QueryClientProvider client={queryClient}>
                <Routes>
                    {/* Redirect root to /home */}
                    <Route path="/" element={<Navigate to="/home" replace />} />

                    {/* Home route with all your existing components */}
                    <Route path="/home" element={<HomePage />} />

                    {/* Privacy Policy route */}
                    <Route path="/docmate-privacy-policy" element={<DocMatePrivacyPolicy />} />

                    {/* Catch-all route - redirect to home */}
                    <Route path="*" element={<Navigate to="/home" replace />} />
                </Routes>
            </QueryClientProvider>
        </>
    );
};

const AppWithTheme = () => {
    const { darkMode } = useDarkMode();
    const theme = createAppTheme(darkMode ? 'dark' : 'light');

    return (
        <ThemeProvider theme={theme}>
            <LanguageAnimationProvider>
                <CssBaseline />
                <Router>
                    <AppContent />
                </Router>
            </LanguageAnimationProvider>
        </ThemeProvider>
    );
};

export default function App() {
    return (
        <React.Suspense fallback="Loading...">
            <DarkModeProvider>
                <AppWithTheme />
            </DarkModeProvider>
        </React.Suspense>
    );
}